import React from 'react';
import SingleSelect from 'components/SingleSelect';
import SearchSelect from 'components/SearchSelect';
import { useTmsSearch } from '../../hooks/useTmsSearch';
import { useFilteredCarriers, isCarrierValidForRouteType } from '../../hooks/useFilteredCarriers';
import { TmsCarrierType } from '../graphql/queries/getTmsCarriers';
import { t } from '../../../../i18n';
import { getSmartLabelClassName } from '../../../../utils/labelWidthUtils';

export interface FilterOptions {
    routeName: string;
    routeType: string;
    carrierId: string;
    startWarehouseId: string;
    endWarehouseId: string;
    waypointWarehouseId: string;
    status: string;
}

export const DEFAULT_FILTER_OPTIONS: FilterOptions = {
    routeName: '',
    routeType: '',
    carrierId: '',
    startWarehouseId: '',
    endWarehouseId: '',
    waypointWarehouseId: '',
    status: ''
};

interface RouteFilterProps {
    filters: FilterOptions;
    onFilterChange: (filters: FilterOptions) => void;
    statusOptions: { label: string; value: number }[];
    routeTypeOptions: { label: string; value: number }[];
    carriersData: TmsCarrierType[] | undefined;
    handoverCarrierOptions: { label: string; value: string }[];
    warehouseOptions: { id: string; sortingCode: string }[];
    currentRouteType?: string; // 当前选中的路线类型（从 tab 传入）
    isHandoverTab?: boolean; // 是否为交接 tab
}

const RouteFilter: React.FC<RouteFilterProps> = ({ filters, onFilterChange, statusOptions, routeTypeOptions, carriersData, handoverCarrierOptions, warehouseOptions, currentRouteType, isHandoverTab }) => {
    const { handleRouteSearch } = useTmsSearch();

    // Convert warehouseOptions to SingleSelect format
    const warehouseSelectOptions = warehouseOptions.map(w => ({ 
        label: w.sortingCode, 
        value: w.id 
    }));

    // Filter carriers based on routeType using the reusable hook
    // 优先使用 currentRouteType，如果没有则使用 filters.routeType
    const effectiveRouteType = currentRouteType || filters.routeType;
    const carrierOptions = useFilteredCarriers(carriersData, effectiveRouteType);

    // 三列布局：按列最宽对齐
    // Row1: route | status | carrier
    // Row2: start | end | waypoint
    const labelRoute = String(t('tms.route.filters.route'));
    const labelStatus = String(t('tms.route.filters.status'));
    const labelCarrier = String(isHandoverTab ? t('tms.route.drawer.handoverCarrier') : t('tms.route.filters.carrier'));
    const labelStart = String(t('tms.route.filters.start'));
    const labelEnd = String(t('tms.route.filters.end'));
    const labelWaypoint = String(t('tms.route.filters.waypoint'));

    const column1Labels = [labelRoute, labelStart];
    const column2Labels = [labelStatus, labelEnd];
    const column3Labels = [labelCarrier, labelWaypoint];

    const handleFilterChange = (name: string, value: any) => {
        let processedValue = value;
        if (typeof value === 'string' && value.trim() === '') {
            processedValue = undefined;
        }
        
        // Special handling for routeType change: check if current carrierId is still valid
        if (name === 'routeType' && value !== undefined && carriersData && filters.carrierId) {
            const isValid = isCarrierValidForRouteType(carriersData, filters.carrierId, value);
            if (!isValid) {
                // Clear carrierId if it's no longer valid
                onFilterChange({ ...filters, [name]: processedValue, carrierId: '' });
                return;
            }
        }
        
        onFilterChange({ ...filters, [name]: processedValue });
    };

    return (
        <div className="mb-2 pl-0 py-2">
            {/* 使用 Grid 布局确保列对齐 */}
            <div className="grid grid-cols-3 gap-x-10 gap-y-4 items-start">
                <div className="min-w-0">
                    <SearchSelect
                        label={labelRoute}
                        placeholder={t('tms.route.filters.searchPlaceholder')}
                        value={filters.routeName}
                        onChange={(value) => {
                            handleFilterChange('routeName', value);
                        }}
                        onSearch={(searchValue, callback) => {
                            // 根据当前 tab 的 routeType 过滤路线搜索
                            const routeType = effectiveRouteType ? Number(effectiveRouteType) : undefined;
                            handleRouteSearch(searchValue, callback, null, true, routeType);
                        }}
                        spanClassName={getSmartLabelClassName(column1Labels)}
                    />
                </div>
                <div className="min-w-0">
                    <SingleSelect
                        label={labelStatus}
                        value={filters.status}
                        onChange={value => handleFilterChange('status', value)}
                        options={statusOptions}
                        spanClassName={getSmartLabelClassName(column2Labels)}
                    />
                </div>
                <div className="min-w-0">
                    <SingleSelect
                        label={labelCarrier}
                        value={filters.carrierId}
                        onChange={value => handleFilterChange('carrierId', value)}
                        options={isHandoverTab ? handoverCarrierOptions : carrierOptions}
                        spanClassName={getSmartLabelClassName(column3Labels)}
                    />
                </div>
                {/* 第二行 */}
                <div className="min-w-0">
                    <SingleSelect
                        label={labelStart}
                        value={filters.startWarehouseId}
                        onChange={value => handleFilterChange('startWarehouseId', value)}
                        options={warehouseSelectOptions}
                        spanClassName={getSmartLabelClassName(column1Labels)}
                    />
                </div>
                <div className="min-w-0">
                    <SingleSelect
                        label={labelEnd}
                        value={filters.endWarehouseId}
                        onChange={value => handleFilterChange('endWarehouseId', value)}
                        options={warehouseSelectOptions}
                        spanClassName={getSmartLabelClassName(column2Labels)}
                    />
                </div>
                <div className="min-w-0">
                    <SingleSelect
                        label={labelWaypoint}
                        value={filters.waypointWarehouseId}
                        onChange={value => handleFilterChange('waypointWarehouseId', value)}
                        options={warehouseSelectOptions}
                        spanClassName={getSmartLabelClassName(column3Labels)}
                    />
                </div>
            </div>
        </div>
    );
};

export default RouteFilter;
